// src/designer-components/ShaTelephony/settings.ts

import { SettingsFormMarkupFactory } from "@shesha-io/reactjs";
import { nanoid } from "nanoid";

const PROVIDER_TYPES = [
  { value: "innovaphone", label: "Innovaphone" },
  { value: "signalr", label: "SignalR" },
  { value: "awsconnect", label: "AWS Connect" },
];

// 0.46.0 removed DesignerToolbarSettings. Settings are built through the typed factory the
// framework passes in, following the shape the framework's own components use:
//
//   * one addSearchableTabs wrapper - Common / Data / Events are the standard keys - so the
//     properties panel is tabbed rather than one long scroll
//   * ids are never written by hand. The builder assigns component ids; a tab and a collapsible
//     panel each need their own, so those are generated and passed to fbf(id) - that is what
//     parents their children, which is why none of the inputs set parentId
//   * inputs go through addSettingsInput / addSettingsInputRow. Note the two spellings:
//     addSettingsInput takes `inputType`, an input nested in a row takes `type`
//   * size: 'small' keeps labels in the designer's type scale
//
// The three provider sections stay as separate collapsible panels inside Data. They were meant to
// show conditionally on providerType - the `visible` rules were commented out before this change
// and are left that way, so all three render at once.
const commonTabId = nanoid();
const dataTabId = nanoid();
const eventsTabId = nanoid();
const signalrPanelId = nanoid();
const innovaphonePanelId = nanoid();
const awsPanelId = nanoid();

export const settingsForm: SettingsFormMarkupFactory = ({ fbf }) => ({
  components: fbf("root")
    .addSearchableTabs({
      propertyName: "settingsTabs",
      parentId: "root",
      label: "Settings",
      hideLabel: true,
      labelAlign: "right",
      size: "small",
      tabs: [
        {
          key: "common",
          title: "Common",
          id: commonTabId,
          components: fbf(commonTabId)
            .addContextPropertyAutocomplete({
              propertyName: "propertyName",
              label: "Property name",
              size: "small",
              styledLabel: true,
              validate: { required: false },
            })
            .addSettingsInputRow({
              inputs: [
                {
                  type: "textField",
                  propertyName: "name",
                  label: "Name",
                  size: "small",
                  validate: { required: true },
                  description: "Component unique identifier",
                  jsSetting: true,
                },
                {
                  type: "switch",
                  propertyName: "hideLabel",
                  label: "Hide Label",
                  size: "small",
                  jsSetting: true,
                },
              ],
            })
            .addSettingsInput({
              inputType: "dropdown",
              propertyName: "providerType",
              label: "Provider Type",
              size: "small",
              validate: { required: true },
              description: "Select the telephony provider to use",
              dropdownOptions: PROVIDER_TYPES,
              jsSetting: true,
            })
            .addSettingsInputRow({
              inputs: [
                {
                  type: "switch",
                  propertyName: "enableRejectCall",
                  label: "Enable RejectCall",
                  size: "small",
                  description: "Display the reject option on incoming calls",
                  jsSetting: true,
                },
                {
                  type: "switch",
                  propertyName: "showDialpad",
                  label: "Show Dialpad",
                  size: "small",
                  description: "Display the dialpad button in the telephony bar",
                  jsSetting: true,
                },
                {
                  type: "switch",
                  propertyName: "showAvailabilityToggle",
                  label: "Show Availability Toggle",
                  size: "small",
                  description: "Display the agent availability toggle in the telephony bar",
                  jsSetting: true,
                },
              ],
            })
            .addSettingsInputRow({
              inputs: [
                {
                  type: "switch",
                  propertyName: "allowOutbound",
                  label: "Allow Outbound Calls",
                  size: "small",
                  description: "Allow outbound calls from this agent this ANDed with the Telephony-Outbound permission",
                  jsSetting: true,
                },
                {
                  type: "switch",
                  propertyName: "allowInbound",
                  label: "Allow Inbound Calls",
                  size: "small",
                  description: "Allow inbound calls to this agent this ANDed with the Telephony-Inbound permission",
                  jsSetting: true,
                },
              ],
            })
            .toJson(),
        },
        {
          key: "data",
          title: "Data",
          id: dataTabId,
          components: fbf(dataTabId)
            .addCollapsiblePanel({
              id: signalrPanelId,
              propertyName: "pnlSignalR",
              parentId: dataTabId,
              label: "SignalR Settings",
              labelAlign: "right",
              expandIconPosition: "start",
              ghost: true,
              collapsible: "header",
              content: {
                id: nanoid(),
                components: fbf(signalrPanelId)
                  .addSettingsInputRow({
                    inputs: [
                      {
                        type: "textField",
                        propertyName: "hubUrl",
                        label: "SignalR Hub URL",
                        size: "small",
                        description: "URL of the SignalR telephony hub (e.g., https://localhost:44362/telephonyHub)",
                        placeholder: "https://localhost:44362/telephonyHub",
                        jsSetting: true,
                      },
                      {
                        type: "textField",
                        propertyName: "apiBaseUrl",
                        label: "API Base URL",
                        size: "small",
                        description: "Base URL for the telephony API calls (e.g., https://localhost:44362)",
                        placeholder: "https://localhost:44362",
                        jsSetting: true,
                      },
                    ],
                  })
                  .toJson(),
              },
            })
            .addCollapsiblePanel({
              id: innovaphonePanelId,
              propertyName: "pnlInnovaphone",
              parentId: dataTabId,
              label: "Innovaphone Settings",
              labelAlign: "right",
              expandIconPosition: "start",
              ghost: true,
              collapsible: "header",
              content: {
                id: nanoid(),
                components: fbf(innovaphonePanelId)
                  .addSettingsInput({
                    inputType: "textField",
                    propertyName: "serverUrl",
                    label: "Server URL",
                    size: "small",
                    description: "URL of the Innovaphone WebAgent server (e.g., https://10.254.254.183/webagent/)",
                    placeholder: "https://10.254.254.183/webagent/",
                    jsSetting: true,
                  })
                  .addSettingsInputRow({
                    inputs: [
                      {
                        type: "textField",
                        propertyName: "username",
                        label: "Default Username",
                        size: "small",
                        description: "Default username for Innovaphone (e.g., 1990). Will be overridden by user's TelephonyProfile if available.",
                        placeholder: "1990",
                        jsSetting: true,
                      },
                      {
                        type: "Password",
                        propertyName: "password",
                        label: "Default Password",
                        size: "small",
                        description: "Default password for Innovaphone (e.g., 1234). Will be overridden by user's TelephonyProfile if available.",
                        placeholder: "1234",
                        jsSetting: true,
                      },
                    ],
                  })
                  .addSettingsInputRow({
                    inputs: [
                      {
                        type: "textField",
                        propertyName: "campaign",
                        label: "Campaign",
                        size: "small",
                        description: "Campaign ID for Innovaphone (e.g., 890)",
                        placeholder: "890",
                        jsSetting: true,
                      },
                      {
                        type: "textField",
                        propertyName: "skill",
                        label: "Skill",
                        size: "small",
                        description: "Skill ID for Innovaphone (e.g., 8890)",
                        placeholder: "8890",
                        jsSetting: true,
                      },
                    ],
                  })
                  .addSettingsInputRow({
                    inputs: [
                      {
                        type: "textField",
                        propertyName: "vdn",
                        label: "VDN",
                        size: "small",
                        description: "VDN for Innovaphone (e.g., 7990)",
                        placeholder: "7990",
                        jsSetting: true,
                      },
                      {
                        type: "textField",
                        propertyName: "extension",
                        label: "Extension",
                        size: "small",
                        description: "Extension for Innovaphone softphone (e.g., 6990)",
                        placeholder: "6990",
                        jsSetting: true,
                      },
                    ],
                  })
                  .toJson(),
              },
            })
            // hubUrl and apiBaseUrl also appear in the SignalR panel above. They are the same
            // model properties, so the two panels edit one value - kept as-is because the
            // provider panels were meant to be mutually exclusive via the commented-out
            // `visible` rules. Restore those (or rename these properties) rather than leaving
            // both editable at once.
            .addCollapsiblePanel({
              id: awsPanelId,
              propertyName: "pnlAwsConnect",
              parentId: dataTabId,
              label: "AWS Connect Settings",
              labelAlign: "right",
              expandIconPosition: "start",
              ghost: true,
              collapsible: "header",
              content: {
                id: nanoid(),
                components: fbf(awsPanelId)
                  .addSettingsInputRow({
                    inputs: [
                      {
                        type: "textField",
                        propertyName: "region",
                        label: "AWS Region",
                        size: "small",
                        description: "AWS Region for AWS Connect (e.g., us-east-1)",
                        placeholder: "us-east-1",
                        jsSetting: true,
                      },
                      {
                        type: "textField",
                        propertyName: "instanceUrl",
                        label: "Instance URL",
                        size: "small",
                        description: "AWS Connect Instance URL",
                        jsSetting: true,
                      },
                    ],
                  })
                  .addSettingsInputRow({
                    inputs: [
                      {
                        type: "textField",
                        propertyName: "hubUrl",
                        label: "SignalR Hub URL",
                        size: "small",
                        description: "URL of the SignalR telephony hub for AWS Connect events",
                        placeholder: "https://localhost:44362/telephonyHub",
                        jsSetting: true,
                      },
                      {
                        type: "textField",
                        propertyName: "apiBaseUrl",
                        label: "API Base URL",
                        size: "small",
                        description: "Base URL for the AWS Connect API calls",
                        placeholder: "https://localhost:44362",
                        jsSetting: true,
                      },
                    ],
                  })
                  .addSettingsInputRow({
                    inputs: [
                      {
                        type: "textField",
                        propertyName: "defaultCountryCode",
                        label: "Default Country Code",
                        size: "small",
                        description: "Country code prefix used to normalize phone numbers (e.g., +27 for South Africa)",
                        placeholder: "+27",
                        jsSetting: true,
                      },
                      {
                        type: "textField",
                        propertyName: "contactFlowId",
                        label: "Contact Flow ID",
                        size: "small",
                        description: "The ID of the contact flow to use for outbound calls (e.g., '12345678-1234-1234-1234-123456789012')",
                        jsSetting: true,
                      },
                    ],
                  })
                  .toJson(),
              },
            })
            .toJson(),
        },
        {
          key: "events",
          title: "Events",
          id: eventsTabId,
          components: fbf(eventsTabId)
            .addSettingsInput({
              inputType: "configurableActionConfigurator",
              propertyName: "onCallStarted",
              label: "On Call Started",
              size: "small",
              description: "Action to execute when a call starts - contains callDetails object",
            })
            .addSettingsInput({
              inputType: "configurableActionConfigurator",
              propertyName: "onCallEnded",
              label: "On Call Ended",
              size: "small",
              description: "Action to execute when a call ends - contains callDetails object",
            })
            .addSettingsInput({
              inputType: "configurableActionConfigurator",
              propertyName: "onAgentStatusChanged",
              label: "On Agent Status Changed",
              size: "small",
              description: "Action to execute when agent status changes - contains agentStatus object",
            })
            .addSettingsInput({
              inputType: "configurableActionConfigurator",
              propertyName: "onCallStatusChanged",
              label: "On Call Status Changed",
              size: "small",
              description: "Action to execute when call status changes - contains callStatus object",
            })
            .addSettingsInput({
              inputType: "configurableActionConfigurator",
              propertyName: "doCallTransfer",
              label: "Call Transfer",
              size: "small",
              description: "Action to execute when a call is transferred - contains transferDetails object",
            })
            .toJson(),
        },
      ],
    })
    .toJson(),
  formSettings: {
    colon: false,
    layout: "vertical",
    labelCol: { span: 24 },
    wrapperCol: { span: 24 },
  },
});
